import { useId, useState } from 'react';

export type LegalPageKey = 'kvkk' | 'cookies' | 'privacy' | 'terms';

export const legalPageByPath: Record<string, LegalPageKey> = {
  '/odevlendir/kvkk': 'kvkk',
  '/odevlendir/cerezler': 'cookies',
  '/odevlendir/gizlilik': 'privacy',
  '/odevlendir/kullanim-kosullari': 'terms',
};

const CONTACT_EMAIL = 'hutame@tekteogren.com.tr';

function ContactLink() {
  return <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>;
}

export function LegalPage({ page }: { page: LegalPageKey }) {
  const content = {
    kvkk: <KvkkNotice />,
    cookies: <CookieNotice />,
    privacy: <PrivacyPolicy />,
    terms: <TermsOfUse />,
  }[page];

  return (
    <article className="legal-page">
      <a className="ghost-button legal-back" href="/odevlendir/">← Oyunla Ödevlendir</a>
      {content}
      <p className="legal-updated">Son güncelleme: 11 Ağustos 2026</p>
    </article>
  );
}

function KvkkNotice() {
  return (
    <>
      <p className="eyebrow">Kişisel veriler</p>
      <h1>KVKK Aydınlatma Metni</h1>
      <p>
        Tekte Öğren olarak biz, Oyunla Ödevlendir hizmetinde işlenen kişisel veriler hakkında bu
        metinle bilgi veriyoruz. Soruların ve kişisel veri başvuruların için <ContactLink /> adresine
        ulaşabilirsin.
      </p>

      <h2>Hangi verileri işliyoruz?</h2>
      <ul>
        <li>Oyun oluşturan kullanıcıların e-posta adresi, güvenli parola özeti ve oturum kayıtları.</li>
        <li>Hazırlanan oyunlar, sorular, seçenekler, ayarlar ve kullanıcı tarafından yüklenen görseller.</li>
        <li>Oyuncunun adı, soyadı, puanı, doğru/yanlış sayısı, deneme sayısı, süre ve tamamlanma durumu.</li>
        <li>Hizmet güvenliği için istek zamanı ve kısa süreli ağ/güvenlik bilgileri.</li>
      </ul>

      <h2>Neden ve nasıl işliyoruz?</h2>
      <p>
        Veriler; hesap ve oturum oluşturmak, eğitsel oyun hazırlamak, süreli bağlantıyı çalıştırmak,
        öğrenci sonucunu öğretmene göstermek, kötüye kullanımı önlemek ve hizmet güvenliğini sağlamak
        amaçlarıyla elektronik ortamda toplanır. İşleme faaliyetleri hizmet ilişkisinin kurulması ve
        yürütülmesi, hukuki yükümlülükler ve hizmet güvenliğine ilişkin meşru menfaatler çerçevesinde;
        ayrıca gerekli olduğu durumlarda uygun izin veya açık rıza temelinde yürütülür.
      </p>

      <h2>Kimlerle paylaşılabilir?</h2>
      <p>
        Sonuçlar yalnız oyunu yayımlayan hesabın panelinde tam adla gösterilir. Kamuya açık ilk 10
        tablosunda soyad yalnız baş harfiyle gösterilir. Veriler, hizmetin barındırılması için Türkiye’deki
        VDS sağlayıcısı ve hukuken zorunlu durumlarda yetkili makamlarla sınırlı olarak paylaşılabilir.
        Alan adı Cloudflare DNS hizmetini kullanır; web trafiği Cloudflare proxy/CDN üzerinden geçmez ve
        doğrudan Türkiye’deki sunucuya ulaşır.
      </p>

      <h2>Ne kadar süre saklıyoruz?</h2>
      <ul>
        <li>Yayın bağlantıları seçilen 1 saat–7 gün sonunda kapanır; kayıt ve yayın görselleri saatlik temizlikte silinir.</li>
        <li>Öğrenci sonuçları yayın bittikten sonra 7 gün öğretmen panelinde tutulur ve sonra silinir.</li>
        <li>Öğretmen oturumu varsayılan olarak 12 saat; öğrenci oyun çerezi en fazla 6 saat yaşar.</li>
        <li>Hesap, taslak ve taslak görselleri silme talebi ile kaldırılana kadar saklanabilir.</li>
        <li>Oyunla Ödevlendir verileri için şu anda ayrıca düzenli bir uygulama yedeği oluşturulmamaktadır.</li>
      </ul>

      <h2>Hakların</h2>
      <p>
        KVKK’nın 11. maddesi kapsamındaki bilgi alma, düzeltme, silme ve diğer taleplerini kimliğini
        doğrulamaya elverişli bilgilerle <ContactLink /> adresine gönderebilirsin. Başvuru sırasında
        yalnız talebin için gerekli bilgileri paylaşmanı öneririz.
      </p>
    </>
  );
}

function CookieNotice() {
  return (
    <>
      <p className="eyebrow">Zorunlu teknolojiler</p>
      <h1>Çerez Bilgilendirmesi</h1>
      <p>
        Oyunla Ödevlendir reklam, analiz veya davranış takibi çerezi kullanmaz. Yalnız hizmetin güvenli
        biçimde çalışması için gereken iki HttpOnly çerez kullanılır.
      </p>
      <div className="table-wrap legal-table">
        <table>
          <thead><tr><th>Çerez</th><th>Amaç</th><th>Süre</th><th>Tür</th></tr></thead>
          <tbody>
            <tr>
              <td><code>odevlendir_session</code></td>
              <td>Oyun oluşturan kullanıcının güvenli oturumunu sürdürür.</td>
              <td>Varsayılan 12 saat</td><td>Zorunlu</td>
            </tr>
            <tr>
              <td><code>odevlendir_play</code></td>
              <td>Öğrencinin başlattığı oyun denemesini güvenli sonuç kaydıyla eşleştirir.</td>
              <td>En fazla 6 saat ve yayın bitişini aşmaz</td><td>Zorunlu</td>
            </tr>
          </tbody>
        </table>
      </div>
      <h2>Güvenlik özellikleri</h2>
      <p>
        Çerezlerin açık değerleri JavaScript tarafından okunamaz. Üretimde Secure, SameSite=Lax ve
        yalnız <code>/odevlendir</code> yolu için kullanılır. Sunucudaki veritabanında açık belirteç yerine
        yalnız kriptografik özet saklanır.
      </p>
      <h2>Çerezleri kapatırsan ne olur?</h2>
      <p>
        Bu çerezler zorunlu olduğu için tercih panelinden kapatılamaz. Tarayıcıdan engellenmeleri hâlinde
        hesap girişi veya öğrenci sonucunun kaydedilmesi çalışmaz. Analitik ve reklam çerezleri kullanılmadığı
        için ayrıca bir pazarlama tercihi tutulmaz.
      </p>
    </>
  );
}

function PrivacyPolicy() {
  return (
    <>
      <p className="eyebrow">Gizlilik</p>
      <h1>Gizlilik Politikası</h1>
      <p>
        Tekte Öğren olarak biz, Oyunla Ödevlendir’i mümkün olan en az veriyle çalıştırmayı ve çocuklara
        ait sonuçları yalnız ilgili öğretmenin erişimine sunmayı amaçlarız. Faaliyete özel ayrıntılar için
        ayrıca <a href="/odevlendir/kvkk">KVKK Aydınlatma Metni</a> incelenmelidir.
      </p>
      <h2>Temel ilkelerimiz</h2>
      <ul>
        <li>Parolalar açık biçimde saklanmaz; güvenli özetleri tutulur.</li>
        <li>Oturum ve oyun denemesi belirteçlerinin yalnız kriptografik özeti veritabanına yazılır.</li>
        <li>Yüklenen görseller tür, boyut ve piksel sınırlarıyla doğrulanır; metadata kaldırılıp WebP’ye dönüştürülür.</li>
        <li>Süreli oyunlar arama motorlarına kapalı ve yalnız tahmin edilmesi güç bağlantılarla erişilebilirdir.</li>
        <li>Kamuya açık tabloda soyadın tamamı gösterilmez; öğretmen sonuçları oturumla korunur.</li>
        <li>Reklam, analitik ve üçüncü taraf takip sistemi kullanılmaz.</li>
      </ul>
      <h2>Çocukların verileri ve yüklenen içerik</h2>
      <p>
        Oyun oluşturan kişi, öğrenci verilerini ve fotoğraf gibi kişisel içerikleri kullanmak için gerekli
        yetkiye sahip olmalıdır. Gereksiz kişisel bilgi, hassas bilgi veya izin alınmamış öğrenci görseli
        yüklenmemelidir. Öğrenci verileri öğretmen sonucu ve kısa süreli sıralama amacı dışında kullanılmaz.
      </p>
      <h2>Barındırma ve yedekleme</h2>
      <p>
        Uygulama ve kalıcı veriler Türkiye merkezli bir VDS’de tutulur. Cloudflare yalnız DNS hizmeti
        sağlar; oyun sitesinin web trafiği Cloudflare proxy/CDN üzerinden geçirilmez. Şu anda ayrıca
        düzenli uygulama yedeği alınmamaktadır.
      </p>
      <h2>İletişim</h2>
      <p>Gizlilik, erişim, düzeltme veya silme taleplerin için <ContactLink /> adresine yazabilirsin.</p>
    </>
  );
}

function TermsOfUse() {
  return (
    <>
      <p className="eyebrow">Hizmet kuralları</p>
      <h1>Kullanım Koşulları</h1>
      <p>
        Oyunla Ödevlendir’i kullanarak aşağıdaki kurallara uymayı kabul edersin. Hizmet ilk sürümünde
        eğitsel oyun hazırlama, süreli bağlantı paylaşma ve kısa süreli sonuç takibi sunar.
      </p>
      <h2>Hesap ve içerik sorumluluğu</h2>
      <ul>
        <li>Hesap bilgilerini doğru tutmak ve parolanı korumak senin sorumluluğundadır.</li>
        <li>Yüklediğin metin ve görselleri kullanma, işleme ve öğrencilerle paylaşma yetkisine sahip olmalısın.</li>
        <li>Hukuka aykırı, zararlı, ayrımcı, müstehcen veya üçüncü kişilerin haklarını ihlal eden içerik yüklenemez.</li>
        <li>Çocuklara ait gereksiz kişisel veri veya izinsiz fotoğraf yüklenmemelidir.</li>
      </ul>
      <h2>Süreli bağlantılar ve sonuçlar</h2>
      <p>
        Oyun bağlantıları seçilen sürenin sonunda otomatik kapanır. Sonuçlar yayın bitiminden sonra 7 gün
        öğretmen panelinde kalır. Kullanıcı aktif bir yayını daha erken kapatabilir. Süresi biten veya
        kaldırılan bağlantının yeniden çalışacağı garanti edilmez.
      </p>
      <h2>Kotalar ve hizmet güvenliği</h2>
      <p>
        Kullanıcı ve platform kotaları hizmet güvenliği için uygulanır. Sistem kotası veya güvenli disk
        payı dolduğunda yeni görsel yükleme geçici olarak durdurulabilir; mevcut içerikler kendiliğinden
        silinmez. Kötüye kullanım, güvenlik riski veya hukuki zorunluluk hâlinde içerik ve hesap erişimi
        sınırlandırılabilir.
      </p>
      <h2>İletişim</h2>
      <p>Bu koşullarla ilgili sorularını <ContactLink /> adresine gönderebilirsin.</p>
    </>
  );
}

export function CookieSettings() {
  const [open, setOpen] = useState(false);
  const titleId = useId();
  return (
    <>
      <button className="footer-link-button" type="button" onClick={() => setOpen(true)}>Çerezleri yönet</button>
      {open && (
        <div className="dialog-backdrop" role="presentation" onMouseDown={(event) => {
          if (event.target === event.currentTarget) setOpen(false);
        }}>
          <section className="auth-dialog cookie-dialog" role="dialog" aria-modal="true" aria-labelledby={titleId}>
            <button className="dialog-close" type="button" onClick={() => setOpen(false)} aria-label="Pencereyi kapat">×</button>
            <p className="eyebrow">Çerez tercihleri</p>
            <h2 id={titleId}>Yalnızca zorunlu çerezler</h2>
            <p className="dialog-description">Reklam, analiz veya takip çerezi kullanmıyoruz.</p>
            <div className="cookie-setting-row">
              <div><strong>Oturum ve oyun güvenliği</strong><small>Giriş yapmayı ve öğrenci sonucunu güvenli biçimde kaydetmeyi sağlar.</small></div>
              <span aria-label="Her zaman açık">Her zaman açık</span>
            </div>
            <p className="cookie-detail">
              Ayrıntılar için <a href="/odevlendir/cerezler">Çerez Bilgilendirmesi</a> sayfasını inceleyebilirsin.
            </p>
            <button className="primary-button auth-submit" type="button" onClick={() => setOpen(false)}>Anladım</button>
          </section>
        </div>
      )}
    </>
  );
}

export function SiteFooter() {
  return (
    <footer className="site-footer">
      <div><strong>Tekte Öğren</strong><span>Oyunla Ödevlendir</span></div>
      <nav aria-label="Yasal ve gizlilik bağlantıları">
        <a href="/odevlendir/kvkk">KVKK Aydınlatma</a>
        <a href="/odevlendir/cerezler">Çerezler</a>
        <a href="/odevlendir/gizlilik">Gizlilik</a>
        <a href="/odevlendir/kullanim-kosullari">Kullanım Koşulları</a>
        <CookieSettings />
      </nav>
    </footer>
  );
}
